import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import WarningIcon from '@material-ui/icons/Warning';
import { withStyles } from '@material-ui/core/styles';

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

const styles = (theme) => ({
  success: {
    color: 'var(--color-feedback-success-900)',
    backgroundColor: 'var(--color-feedback-success-100)',
  },
  info: {
    color: 'var(--color-support-neutral-900)',
    backgroundColor: 'var(-color-brand-primary-light)',
  },
  warning: {
    color: 'var(--color-feedback-warning-900)',
    backgroundColor: 'var(--color-feedback-warning-100)',
  },
  error: {
    color: 'var(--color-feedback-error-900)',
    backgroundColor: 'var(--color-feedback-error-100)',
  },
  icon: {
    fontSize: 24,
  },
  iconVariant: {
    opacity: 0.9,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '450px',
    gap: 'var(--spacing-4xs)',
  },
});

function CustomSnackbarContent(props) {
  const { classes, className, message, onClose, variant, ...other } = props;
  const Icon = variantIcon[variant];

  return (
    <SnackbarContent
      className={classNames(classes[variant], className)}
      aria-describedby='client-snackbar'
      message={
        <span id='client-snackbar' className={classes.message}>
          <Icon className={classNames(classes.icon, classes.iconVariant)} />
          {message}
        </span>
      }
      action={[
        <IconButton
          key='close'
          aria-label='Close'
          color='inherit'
          className={classes.close}
          onClick={onClose}
        >
          <CloseIcon className={classes.icon} />
        </IconButton>,
      ]}
      {...other}
    />
  );
}

CustomSnackbarContent.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  message: PropTypes.node,
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['success', 'warning', 'error', 'info']).isRequired,
};

const MySnackbarContentWrapper = withStyles(styles)(CustomSnackbarContent);

const notificationStyles = (theme) => ({
  root: {
    top: 70,
  },
});

class CustomizedSnackBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: props.open,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open) {
      this.setState({ open: this.props.open });
    }
  }

  handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    this.setState({ open: false });
    if (this.props.onClose) {
      this.props.onClose(event, reason);
    }
  };

  render() {
    const { classes, className, message, variant, autoHideDuration } = this.props;
    const { open } = this.state;

    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={this.handleClose}
        className={classNames(classes.root, className)}
      >
        <MySnackbarContentWrapper
          onClose={this.handleClose}
          variant={variant}
          message={message}
        />
      </Snackbar>
    );
  }
}

CustomizedSnackBar.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool,
  variant: PropTypes.oneOf(['success', 'warning', 'error', 'info']).isRequired,
  message: PropTypes.node,
  onClose: PropTypes.func,
  autoHideDuration: PropTypes.number,
};

CustomizedSnackBar.defaultProps = {
  open: false,
  variant: 'success',
  message: '',
  autoHideDuration: 6000,
  onClose: () => {},
};

export default withStyles(notificationStyles)(CustomizedSnackBar);
